'use client';

import React, {useEffect} from 'react';
import {useSelector, shallowEqual} from 'react-redux';
import {ThemeProvider, Box} from '@mui/material';
import {OptumDefault} from '@optum-rx-skyline/themes';
import init from '@optum-rx-skyline/runtime-themes';
import {Header, AccessWrapper, SessionExpiredAlert} from '@optum-rx-pbm/rx-customsite-utah-components';
import {usePathname, useRouter} from 'next/navigation';

import {HeaderLink} from '../redux/services/types';
import {NON_PROTECTED_ROUTES} from '../common/constants';

import SkeltonLoader from './loading';
import useAemSessionLookup from './common-hooks/useAemSessionLookup';
import {usePropsProvider} from './props-provider';

export default function AppShell({children}: {children: React.ReactNode}) {
  const pathname = usePathname();
  const propsProvider = usePropsProvider();
  const {isLoading, data, isLoggedIn, loggedInUser} = useAemSessionLookup(pathname, propsProvider);
  const router = useRouter();

  const isPreloginRoute = pathname?.startsWith('/prelogin') ?? false;
  const showLoggedInUI = isLoggedIn && !isPreloginRoute;
  const headerList = useSelector(
    (state: {
      lookupDataApi: {
        queries: Record<string, {data?: {headerData?: HeaderLink[]}; error?: {headerData: HeaderLink[]}}>;
      };
    }) => {
      const queries = Object.values(state.lookupDataApi.queries);
      if (queries[0]?.error?.headerData) {
        return queries[0]?.error?.headerData ?? [];
      }
      return queries[0]?.data?.headerData ?? [];
    },
    shallowEqual
  );
  useEffect(() => {
    if (headerList.length && !headerList.map((item) => item.url).includes(pathname)) {
      router.push('/home');
    }
  }, [pathname, headerList, router]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  useEffect(() => {
    if (!window.runtimeThemes) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      init();
    }
    if (window.runtimeThemes) {
      const runtimeThemes = window.runtimeThemes.requireTheme({
        url: 'https://microproducts.optumrx.com/pmpwebcomponents/@optum-rx-skyline/optum-theme/next/latest/optum-theme.es.js',
        themeName: 'optumTheme'
      });

      runtimeThemes
        .then((theme) => {
          // Add any logic for when the theme is loaded
          // eslint-disable-next-line no-console
          console.log('theme loaded', theme);
          const themeLoadedEvent = new CustomEvent('themeLoaded', {detail: theme});
          window.dispatchEvent(themeLoadedEvent);
        })
        .catch((err: unknown) => {
          // Add any logic for when loading theme failed
          // eslint-disable-next-line no-console
          console.log('error loading theme', err);
        });
    }
  }, []);

  return (
    <ThemeProvider theme={OptumDefault}>
      <Box sx={{display: 'flex', flexDirection: 'column', minHeight: '100vh', width: '100%', margin: 'auto'}}>
        {isLoading ? (
          <SkeltonLoader />
        ) : (
          <>
            <Header
              brandLogo={data?.brandLogo ?? ''}
              subBrandLogo={data?.subBrandLogo ?? ''}
              title={data?.brandTitle ?? ''}
              isLoggedIn={showLoggedInUI}
              links={headerList}
              announcementBanner={data?.headerContent?.announcementBanner}
              logoutLabel={data?.headerContent?.logoutLabel ?? ''}
              brandLogoAltText={data?.headerContent?.brandLogoAltText ?? ''}
              subBrandLogoAltText={data?.headerContent?.subBrandLogoAltText ?? ''}
              loggedInUser={loggedInUser}
            />
            <AccessWrapper
              headerList={headerList}
              nonProtectedRoutes={NON_PROTECTED_ROUTES}
              noAccessHeading={data?.noAccess?.noAccessHeading ?? ''}
              noAccessDesc={data?.noAccess?.noAccessDesc ?? ''}
            >
              {children}
            </AccessWrapper>
            {showLoggedInUI && (
              <SessionExpiredAlert
                headerContent={data?.timeout}
                ohidRefreshTokenBeforeExpireIn={propsProvider.ohidRefreshTokenBeforeExpireIn}
                ohidSessionCheckInterval={propsProvider.ohidRefreshTokenBeforeExpireIn}
                inactiveTime={propsProvider.inactiveTime}
                countdownTime={propsProvider.countdownTime}
                authProxyBaseURL={propsProvider.authProxyBaseURL}
              />
            )}
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}
